import i18n from '@/libs/i18n'
import store from '@/store'
import useEntityCrud from '@/composable/useEntityCrud'
import useEntityHelper from '@/composable/useEntityHelper'
import useErrorManager from '@/composable/useErrorManager'
import { ref } from '@vue/composition-api'
import router from '@/router/instance'

export default function useArticlesList() {
  const MODULE_NAME = 'article-categories'
  let ENDPOINT = ''
  if (router.history.current.name === 'pages-articles-list') {
    ENDPOINT = 'articles/search'
  } else if (router.history.current.name === 'pages-articles-add') {
    ENDPOINT = 'articles'
  } else if (router.history.current.name === 'pages-articles-edit') {
    ENDPOINT = 'articles'
  } else if (router.history.current.name === 'pages-article-categories-list') {
    ENDPOINT = 'articles-categories/search'
  } else if (router.history.current.name === 'pages-article-categories-add') {
    ENDPOINT = 'articles-categories'
  } else if (router.history.current.name === 'pages-article-categories-edit') {
    ENDPOINT = 'articles-categories'
  } else {
    ENDPOINT = 'articles'
  }
  const errorManager = useErrorManager()
  const { getAlt, createTagIdArray } = useEntityHelper()
  function convertResource(resource, selectedLang) {
    let watermark = resource.watermark || false
    if (resource.watermark && typeof resource.watermark === 'object') {
      watermark = resource.watermark[selectedLang] !== undefined ? resource.watermark[selectedLang] : false
    }
    return {
      ...resource,
      title: resource.title ? resource.title[selectedLang] : '',
      metaTitle: resource.metaTitle ? resource.metaTitle[selectedLang] : '',
      keywords: resource.keywords ? resource.keywords[selectedLang] : '',
      description: resource.description ? resource.description[selectedLang] : '',
      content: resource.content ? resource.content[selectedLang] : '',
      lang: selectedLang,
      tags: resource.tagIds && resource.tagIds.length ? resource.tagIds.filter(itemEntry => itemEntry && itemEntry.language === selectedLang) : null,
      user: resource.creatorId,
      canonicalUrl: resource.canonicalUrl,
      approver: resource.approverId ? {
        _id: resource.approverId._id,
        slug: resource.approverId.slug ? resource.approverId.slug : '',
        firstName: resource.approverId.firstName ? resource.approverId.firstName[selectedLang] : '',
        lastName: resource.approverId.lastName ? resource.approverId.lastName[selectedLang] : '',
        managerImageFileId: resource.approverId.managerImageFileId,
      } : null,
      parentCategory: resource.parentId && typeof resource.parentId === 'object' ? {
        _id: resource.parentId._id,
        title: resource.parentId.title ? resource.parentId.title[selectedLang] : '',
      } : null,
      writer: resource.writerId,
      editor: resource.editorId,
      relatedArticles: resource.relatedArticleIds && resource.relatedArticleIds.length
        ? resource.relatedArticleIds.map(itemEntry2 => ({
          _id: itemEntry2._id,
          title: itemEntry2.title ? itemEntry2.title[selectedLang] : '',
          articleThumbnailFileId: itemEntry2.articleThumbnailFileId,
        })) : [],
      relatedQuestions: resource.relatedQuestionIds && resource.relatedQuestionIds.length
        ? resource.relatedQuestionIds.map(itemEntry3 => ({
          _id: itemEntry3._id,
          title: itemEntry3.question ? itemEntry3.title[selectedLang] : '',
        })) : [],
      relatedServices: resource.relatedServiceIds && resource.relatedServiceIds.length
        ? resource.relatedServiceIds.map(itemEntry4 => ({
          _id: itemEntry4._id,
          title: itemEntry4.title ? itemEntry4.title[selectedLang] : '',
          serviceThumbnailFileId: itemEntry4.serviceThumbnailFileId,
        })) : [],
      relatedCategories: resource.relatedCategoryIds && resource.relatedCategoryIds.length
        ? resource.relatedCategoryIds.map(itemEntry5 => ({
          _id: itemEntry5._id,
          title: itemEntry5.title ? itemEntry5.title[selectedLang] : '',
        })) : [],
      articleCategoryThumbnail: resource.articleCategoryThumbnailFileId ? resource.articleCategoryThumbnailFileId[selectedLang] : '',
      articleCategoryImage: resource.articleCategoryImageFileId ? resource.articleCategoryImageFileId[selectedLang] : '',
      articleCategoryIcon: resource.articleCategoryIconFileId ? resource.articleCategoryIconFileId[selectedLang] : '',
      articleCategoryThumbnailAlt: getAlt('articleCategoryThumbnailFileId', resource, selectedLang),
      articleCategoryImageAlt: getAlt('articleCategoryImageFileId', resource, selectedLang),
      articleCategoryIconAlt: getAlt('articleCategoryIconFileId', resource, selectedLang),
      articleCategoryLink: { name: 'pages-article-categories-edit', params: { id: resource._id, lang: selectedLang } },
      category: resource.mainParent ? {
        _id: resource.mainParent._id,
        title: resource.mainParent.title ? resource.mainParent.title[selectedLang] : '',
      } : null,
      watermark,
    }
  }

  const entity = useEntityCrud(MODULE_NAME, ENDPOINT, convertResource)
  const { itemLoading, item, form } = entity
  const isUniqueSlug = ref(true)
  const approverOptions = ref([])
  const questionOptions = ref([])
  const serviceOptions = ref([])
  const categoryOptions = ref([])
  const categoryOptions1 = ref([])
  const rootCategoryOptions = ref([])

  const tagsOptions = ref([])
  const articleOptions = ref([])
  const articleOptions1 = ref([])

  // Table Handlers
  const tableColumns = [
    {
      key: 'title', label: i18n.t('general.title'), textKey: 'title', imageKey: 'articleCategoryThumbnail', type: 'textImage', linkKey: 'articleCategoryLink', sortable: true,
    },
    { key: 'user', label: i18n.t('general.creator'), type: 'user' },
    {
      key: 'approver', label: i18n.t('general.approver'), type: 'manager', icon: 'UserCheckIcon',
    },
    { key: 'articleCategoryImage', label: i18n.t('general.cover'), type: 'image' },
    {
      key: 'createdAt', label: i18n.t('general.createdAt'), type: 'dateTime', sortable: true,
    },
    {
      key: 'updatedAt', label: i18n.t('general.updatedAt'), type: 'dateTime', sortable: true,
    },
    { key: 'actions', label: i18n.t('general.actions'), sortable: false },
  ]

  const checkSlugIsUnique = (lang = 'fa', slug) => {
    if (slug === '' || (item.value && item.value.slug === form.value.slug)) {
      isUniqueSlug.value = true
      return
    }
    itemLoading.value = true
    store
      .dispatch('crud/fetchItem',
        { data: { id: slug }, queryParams: { lang }, endpoint: 'articles-categories/slug' })
      .then(response => {
        isUniqueSlug.value = response.data.isUnique
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchArticlesList = (lang = '', search = '', searchBy = '') => {
    itemLoading.value = true
    articleOptions1.value = []
    store
      .dispatch('crud/fetchList', {
        queryParams: { lang, search, searchBy },
        endpoint: 'articles',
      })
      .then(response => {
        articleOptions1.value = response.data.data.map(itemEntry6 => ({ _id: itemEntry6._id, title: itemEntry6.title[lang], articleThumbnailFileId: itemEntry6.articleThumbnailFileId }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchArticles = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    articleOptions.value = []
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, type: 'article',
        },
        endpoint: 'articles/relatedArticles/',
      })
      .then(response => {
        articleOptions.value = response.data.data.map(itemEntry7 => ({ _id: itemEntry7._id, title: itemEntry7.title[lang], articleThumbnailFileId: itemEntry7.articleThumbnailFileId }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchCategoriesList = (lang = 'fa', search = '', searchBy = '', parentId = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40, parentId,
        },
        endpoint: 'articles-categories/search/',
      })
      .then(response => {
        if (parentId !== 'null') {
          categoryOptions1.value = response.data.data.map(itemEntry8 => ({ _id: itemEntry8._id, title: itemEntry8.title[lang] }))
        } else {
          rootCategoryOptions.value = response.data.map(itemEntry9 => ({ _id: itemEntry9._id, title: itemEntry9.title[lang] }))
        }
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchCategories = (lang = 'fa', search = '', searchBy = '', parentId = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40, parentId,
        },
        endpoint: 'articles-categories',
      })
      .then(response => {
        if (parentId !== 'null') {
          categoryOptions.value = response.data.data.map(itemEntry0 => ({ _id: itemEntry0._id, title: itemEntry0.title[lang] }))
        } else {
          rootCategoryOptions.value = response.data.map(itemEntry1 => ({ _id: itemEntry1._id, title: itemEntry1.title[lang] }))
        }
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchServices = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40,
        },
        endpoint: 'services',
      })
      .then(response => {
        serviceOptions.value = response.data.data.map(itemEntry10 => ({ _id: itemEntry10._id, title: itemEntry10.title[lang], serviceThumbnailFileId: itemEntry10.serviceThumbnailFileId }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchQuestions = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40,
        },
        endpoint: 'questions',
      })
      .then(response => {
        questionOptions.value = response.data.data.map(itemEntry11 => ({ _id: itemEntry11._id, title: itemEntry11.title[lang] }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchApprovers = (lang = 'fa', search = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: { lang, search },
        endpoint: 'managers',
      })
      .then(response => {
        approverOptions.value = response.data.data.map(itemEntry12 => ({
          _id: itemEntry12._id,
          firstName: itemEntry12.firstName[lang],
          lastName: itemEntry12.lastName[lang],
          managerImageFileId: itemEntry12.managerImageFileId,
        }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  return {
    ...entity,
    approverOptions,
    articleOptions,
    categoryOptions,
    categoryOptions1,
    checkSlugIsUnique,
    convertResource,
    createTagIdArray,
    errorManager,
    fetchApprovers,
    fetchArticlesList,
    fetchArticles,
    fetchCategories,
    fetchCategoriesList,
    fetchQuestions,
    fetchServices,
    isUniqueSlug,
    questionOptions,
    rootCategoryOptions,
    serviceOptions,
    tableColumns,
    tagsOptions,
  }
}
